import { createAsyncThunk } from "@reduxjs/toolkit";

import { trackInteractionEvent } from "../tracking/googleAnalytics";
import * as API from "../utils/APIrequests";
import {
	mapResponseToMainProduct,
	mapResponseToRecommendedProducts,
	mapResponseToConfigServices,
	mapResponseToSubscription,
} from "../utils/helpers/mapper";
import strings from "../utils/strings";

type DataPayloadI = {
	productId: number;
	price: number;
	taxRate?: number;
	isConfigBundle?: boolean;
	renewalPeriod?: number;
};

type GetPopupDataI = {
	productId: number;
	isAddToCartDrawer?: boolean;
	renewalPeriod?: number;
    categoryId: string;
	configBundleConfigurationId?: string;
};

type GetSubscriptionDataI = DataPayloadI & {
	subGroup: string;
	isConfigServiceIncluded?: boolean;
	configServices?: any;
	configBundleConfigurationId?: string;
};

export const setPopupEnabled = createAsyncThunk(
	"popup/enable",
	async (isEnabled: boolean) => {
		const response = await API.setPopupEnabled(isEnabled);

		return response;
	},
);

export const changeInsurance = createAsyncThunk(
	"product/changeInsurance",
	async ({
		cartId,
		cartItemId,
		insuranceId,
	}: {
		cartId: string;
		cartItemId: string;
		insuranceId: string;
	}) => {
		try {
			const response = await API.switchInsurance(
				cartId,
				cartItemId,
				insuranceId,
			);

			return { ...response, insuranceId };
		} catch {
			console.log("error");
			return null;
		}
	},
);

export const setConfigService = createAsyncThunk(
	"product/configService/set",
	async ({
		cartItemId,
		serviceId,
		optionIds,
	}: {
		cartItemId: string;
		serviceId: string;
		optionIds: string[];
	}) => {
		const response = await API.setConfigService(
			cartItemId,
			serviceId,
			optionIds,
		);

		const returned = mapResponseToConfigServices({
			data: response,
			selectedItem: cartItemId,
		});

		return returned;
	},
);

export const removeConfigService = createAsyncThunk(
	"product/configService/remove",
	async ({
		cartItemId,
		serviceId,
		optionIds,
	}: {
		cartItemId: string;
		serviceId: string;
		optionIds: string[];
	}) => {
		const response = await API.removeConfigService(
			cartItemId,
			serviceId,
			optionIds,
		);

		const returned = mapResponseToConfigServices({
			data: response,
			selectedItem: cartItemId,
		});

		return returned;
	},
);

export const getSubscriptionTermsData = createAsyncThunk(
	"product/getSubscriptionTermsData",
	async ({
		productId,
		selectedConfigServiceIds,
	}: {
		productId: number;
		selectedConfigServiceIds: string[];
	}) => {
		const response = await API.getSubscriptionTermsData({
			productId,
			selectedConfigServiceIds,
		});

		return response;
	},
);

export const getProductData = createAsyncThunk(
	"product/getData",
	async (
		{ productId, renewalPeriod, isAddToCartDrawer, categoryId, configBundleConfigurationId }: GetPopupDataI,
		{ rejectWithValue },
	) => {
		try {
			const response = await API.getPopupData({
				productId,
				renewalPeriod,
                categoryId,
				configBundleConfigurationId
			});

			trackInteractionEvent({
				category: "CartPopup",
				action: "GetPopupData",
				label: "Success",
			});

			return {
				products: {
					isVisible: true,
					mainProduct: mapResponseToMainProduct(response),
					recommendedProducts: {
						products: response.recommendedProducts.map((product) =>
							mapResponseToRecommendedProducts(product),
						),
					},
					chosenProducts: [],
				},
				recommendedProductsHeader: response.recommendedProductsHeader,
				canCustomerDisablePopup: response.canCustomerDisablePopup,
				currencyCode: response.currencyCode,
				singleFlexInsurance: response.singleFlexInsurance,
				containsFlexInsurance: response.containsFlexInsurance,
				showSubscriptionDataOnly: false,
				features: response.features,
			};
		} catch {
			trackInteractionEvent({
				category: "CartPopup",
				action: "GetPopupData",
				label: "Failure",
			});

			return rejectWithValue({
				error: {
					isError: true,
					level: "warning",
					errorMessage: strings["addtocartpopup.general.error"],
				},
			});
		}
	},
);

export const getSubscriptionData = createAsyncThunk(
	"product/getSubscription",
	async (
		{
			productId,
			price,
			taxRate,
			isConfigBundle,
			subGroup,
			renewalPeriod,
			isConfigServiceIncluded,
			configServices,
			configBundleConfigurationId
		}: GetSubscriptionDataI,
		{ rejectWithValue },
	) => {
		try {
			const response = await API.getSubscriptionData({
				productId,
				price,
				taxRate,
				isConfigBundle,
				subGroup,
				renewalPeriod,
				isConfigServiceIncluded,
				configServices,
				configBundleConfigurationId
			});

			trackInteractionEvent({
				category: "CartPopup",
				action: "GetSubscriptionData",
				label: "Success",
			});

			return {
				products: {
					isVisible: true,
					mainProduct: mapResponseToSubscription(response),
				},
				showSubscriptionDataOnly: true,
			};
		} catch {
			trackInteractionEvent({
				category: "CartPopup",
				action: "GetSubscriptionData",
				label: "Failure",
			});

			return rejectWithValue({
				error: {
					isError: true,
					level: "info",
					errorMessage: strings["addtocartpopup.subscription.general.error"],
				},
			});
		}
	},
);
